import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { AttachFile } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useCreateVoucherImageMutation } from "../../store/api";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";

interface TalonarioVoucherProps {
  raffleId: string;
  purchaseOrderId: string;
  voucherUploaded: boolean;
  onUploaded: () => void;
}

// En talonario el comprobante es obligatorio en el link de tickets para validar la compra
export const TalonarioVoucher = ({ raffleId, purchaseOrderId, voucherUploaded, onUploaded }: TalonarioVoucherProps) => {
  const accountId = useAppSelector(({ account }: RootState) => account.id);
  const [createVoucher, { isLoading }] = useCreateVoucherImageMutation();
  // Evita que el navegador muestre la versión en caché al reemplazar el comprobante
  const [cacheBust, setCacheBust] = useState(Date.now());

  const handleFileChange = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    try {
      await createVoucher({
        accountId,
        raffleId,
        imageName: purchaseOrderId,
        imageFile: file,
      }).unwrap();
      enqueueSnackbar("Comprobante enviado. Validaremos tu compra pronto.", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
      setCacheBust(Date.now());
      onUploaded();
    } catch {
      enqueueSnackbar("No pudimos subir tu comprobante, intenta nuevamente.", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    }
  };

  const uploadButton = (label: string, variant: "contained" | "outlined") => (
    <Button
      variant={variant}
      component="label"
      fullWidth
      disabled={isLoading}
      startIcon={isLoading ? <CircularProgress size={18} /> : <AttachFile />}
      sx={{ mt: 1 }}
    >
      {label}
      <input
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          handleFileChange(e.target.files);
          e.target.value = "";
        }}
      />
    </Button>
  );

  if (!voucherUploaded) {
    return (
      <Box mt={2}>
        <Alert severity="warning">
          Falta tu comprobante de pago. Adjúntalo para que podamos validar tu compra.
        </Alert>
        {uploadButton("Adjuntar comprobante", "contained")}
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Alert severity="success">Comprobante enviado ✓</Alert>
      <img
        src={`https://storage.googleapis.com/${accountId}/raffles/${raffleId}/vouchers/${purchaseOrderId}?t=${cacheBust}`}
        alt="Comprobante"
        style={{ width: "100%", marginTop: 10, borderRadius: 8 }}
      />
      {uploadButton("Reemplazar comprobante", "outlined")}
    </Box>
  );
};
